import { useCallback, useMemo, useState } from 'react';

import { ToggleGroup } from '@bambi/component-library/src/components/ToggleGroup/ToggleGroup';

import {
  GEO_HYBRID_MAP_RESOURCE,
  GEO_MAP_RESOURCE,
} from 'src/common/external/aws';

export type MapStyle = 'map' | 'hybrid';

const MAP_STYLE_TITLES = ['Map', 'Satellite'];

const MAP_STYLE_VALUES: MapStyle[] = ['map', 'hybrid'];

const MAP_STYLE_TO_RESOURCE: Record<MapStyle, string> = {
  map: GEO_MAP_RESOURCE,
  hybrid: GEO_HYBRID_MAP_RESOURCE,
};

interface MapStyleToggleProps {
  selectedStyle: MapStyle;
  onSelect: (newStyle: MapStyle) => void;
}

/**
 * Parent should manage state of the selected map style,
 * typically using a useState hook.
 */
export function MapStyleToggle({
  selectedStyle,
  onSelect,
}: MapStyleToggleProps) {
  const selectedIndex = MAP_STYLE_VALUES.indexOf(selectedStyle);

  const onSelectIndex = useCallback(
    (newIndex: number) => {
      onSelect(MAP_STYLE_VALUES[newIndex]);
    },
    [onSelect]
  );

  return (
    <div
      data-testid="map-style-toggle"
      className="absolute top-2.5 right-2.5 inline-block whitespace-nowrap"
    >
      <ToggleGroup
        options={MAP_STYLE_TITLES}
        selectedIndex={selectedIndex}
        onSelect={onSelectIndex}
        equalWidth
      />
    </div>
  );
}

interface MapStyleState {
  selectedMapStyle: MapStyle;
  mapStyleResource: string;
  onSelect: (newStyle: MapStyle) => void;
}

/**
 * Translates between the map style, selected index, and the map style resource string.
 * Returns the values needed to render the MapStyleToggle component
 * and the mapStyleResource string for BambiMapView.
 */
export const useMapStyleState = (defaultMapStyle: MapStyle): MapStyleState => {
  const [mapStyle, setMapStyle] = useState<MapStyle>(defaultMapStyle);

  const mapStyleResource = useMemo(() => {
    return MAP_STYLE_TO_RESOURCE[mapStyle];
  }, [mapStyle]);

  return {
    selectedMapStyle: mapStyle,
    onSelect: setMapStyle,
    mapStyleResource,
  };
};
